import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MapPin,
  Search,
  X,
  Briefcase,
  Clock,
  Share2,
  Bookmark,
  ArrowUpRight,
  Building2,
  Euro,
  CheckCircle,
  ChevronRight,
  Users,
} from 'lucide-react';
import SEO from '../components/SEO';
import { PageHero } from '../components/PageComponents';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { vacaturesData } from '../data/mockData';

// Colored badge per discipline for the list icons
const disciplineColors = {
  Finance: 'bg-[#00897B] text-white',
  HR: 'bg-[#0d1b2a] text-white',
  Duurzaamheid: 'bg-[#2e7d32] text-white',
};

const timeAgo = (dateStr) => {
  const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / (1000 * 60 * 60 * 24));
  if (diff === 0) return 'Vandaag geplaatst';
  if (diff === 1) return '1 dag geleden';
  if (diff < 30) return `${diff} dagen geleden`;
  const months = Math.floor(diff / 30);
  return `${months} ${months === 1 ? 'maand' : 'maanden'} geleden`;
};

// Extra details per vacature (computed, consistent)
const enrich = (v) => ({
  company:
    v.discipline === 'Finance'
      ? 'Toonaangevende opdrachtgever'
      : v.discipline === 'HR'
      ? 'Internationale organisatie'
      : 'Duurzame koploper',
  hours: '32 - 40 uur',
  salary:
    v.level === 'Directie'
      ? '€ 9.000 - € 13.000'
      : v.level === 'Senior'
      ? '€ 5.500 - € 8.000'
      : '€ 3.500 - € 5.000',
  qualifications: [
    v.discipline === 'Finance'
      ? 'HBO/WO opleiding Bedrijfseconomie of Accountancy'
      : v.discipline === 'HR'
      ? 'HBO/WO opleiding Human Resource Management'
      : 'HBO/WO opleiding Sustainability, ESG of gelijkwaardig',
    `Minimaal ${v.level === 'Junior' ? '1' : v.level === 'Medior' ? '3' : '5'}+ jaar relevante werkervaring`,
    'Uitstekende beheersing van de Nederlandse en Engelse taal',
    'Pro-actieve, oplossingsgerichte en communicatieve houding',
    v.discipline === 'Finance'
      ? 'Ervaring met ERP-systemen (SAP, Oracle, Exact of gelijkwaardig)'
      : v.discipline === 'HR'
      ? 'Kennis van AFAS of SuccessFactors is een pré'
      : 'Kennis van CSRD, ESRS en GHG-protocol',
  ],
  responsibilities: [
    'Verantwoordelijk voor het operationele proces binnen de afdeling',
    'Samenwerken met een multidisciplinair team van professionals',
    'Analyseren, rapporteren en optimaliseren van processen',
    'Adviseren van het management over verbetermogelijkheden',
    'Begeleiden en coachen van junior teamleden',
  ],
  benefits: [
    'Marktconform salaris afhankelijk van ervaring',
    'Uitstekende secundaire arbeidsvoorwaarden',
    'Doorgroei- en opleidingsmogelijkheden',
    'Professionele begeleiding vanuit Staffing Force',
    'Een dynamische werkomgeving met impact',
  ],
});

const VacatureListCard = ({ vac, isSelected, onClick }) => (
  <motion.div
    layout
    onClick={onClick}
    whileHover={{ y: -2 }}
    className={`cursor-pointer rounded-xl border transition-all p-5 ${
      isSelected
        ? 'bg-white border-[#00897B] shadow-lg shadow-[#00897B]/10'
        : 'bg-white border-gray-100 hover:border-[#00897B]/40 hover:shadow-sm'
    }`}
    data-testid={`vacature-list-card-${vac.id}`}
  >
    <div className="flex items-start gap-3">
      <div
        className={`w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0 font-bold text-sm ${
          disciplineColors[vac.discipline] || 'bg-gray-800 text-white'
        }`}
      >
        {vac.discipline.slice(0, 2).toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <h3
          className={`font-semibold text-[15px] leading-snug mb-1 line-clamp-2 transition-colors ${
            isSelected ? 'text-[#00897B]' : 'text-gray-900'
          }`}
        >
          {vac.title}
        </h3>
        <p className="text-xs text-gray-500 mb-3">{enrich(vac).company}</p>

        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[11px] text-gray-600">
          <span className="flex items-center gap-1">
            <MapPin size={11} className="text-[#00897B]" />
            {vac.location}
          </span>
          <span className="flex items-center gap-1">
            <Briefcase size={11} className="text-[#00897B]" />
            {vac.type}
          </span>
          <span className="flex items-center gap-1">
            <Clock size={11} className="text-[#00897B]" />
            {vac.level}
          </span>
        </div>

        <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
          <span className="text-[11px] text-gray-400">{timeAgo(vac.date)}</span>
        </div>
      </div>
    </div>
  </motion.div>
);

const VacatureDetail = ({ vac }) => {
  if (!vac) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
        <Briefcase size={48} className="mx-auto text-gray-200 mb-4" />
        <p className="text-gray-500">Selecteer een vacature om de details te bekijken</p>
      </div>
    );
  }
  const extra = enrich(vac);
  return (
    <motion.div
      key={vac.id}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="bg-white rounded-2xl border border-gray-100 overflow-hidden"
      data-testid="vacature-detail-panel"
    >
      {/* Top bar */}
      <div className="p-6 md:p-8 border-b border-gray-100">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-4 flex-1 min-w-0">
            <div
              className={`w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 font-bold ${
                disciplineColors[vac.discipline] || 'bg-gray-800 text-white'
              }`}
            >
              {vac.discipline.slice(0, 2).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight">
                {vac.title}
              </h2>
              <p className="text-sm text-gray-500 mt-1 flex items-center gap-2">
                <Building2 size={14} />
                {extra.company}
                <span className="w-1 h-1 rounded-full bg-gray-300" />
                {timeAgo(vac.date)}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              className="p-2 rounded-lg border border-gray-200 text-gray-500 hover:text-[#00897B] hover:border-[#00897B]/30 transition-colors"
              aria-label="Delen"
              data-testid="vacature-share-btn"
            >
              <Share2 size={16} />
            </button>
            <button
              className="p-2 rounded-lg border border-gray-200 text-gray-500 hover:text-[#00897B] hover:border-[#00897B]/30 transition-colors"
              aria-label="Opslaan"
              data-testid="vacature-save-btn"
            >
              <Bookmark size={16} />
            </button>
          </div>
        </div>

        {/* Key facts */}
        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="flex items-center gap-2 text-sm text-gray-700">
            <MapPin size={16} className="text-[#00897B] flex-shrink-0" />
            <span>{vac.location}</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-700">
            <Briefcase size={16} className="text-[#00897B] flex-shrink-0" />
            <span>{vac.type}</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-700">
            <Clock size={16} className="text-[#00897B] flex-shrink-0" />
            <span>{extra.hours}</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-700">
            <Euro size={16} className="text-[#00897B] flex-shrink-0" />
            <span>{extra.salary}</span>
          </div>
        </div>

        {/* CTAs */}
        <div className="mt-6 flex flex-col sm:flex-row gap-3">
          <Link
            to={`/vacatures/${vac.id}`}
            className="flex-1 bg-[#00897B] hover:bg-[#00796B] text-white px-6 py-3 rounded-lg font-semibold text-sm text-center transition-all hover:-translate-y-0.5 hover:shadow-lg hover:shadow-[#00897B]/25 inline-flex items-center justify-center gap-2"
            data-testid="vacature-apply-btn"
          >
            Direct solliciteren
            <ArrowUpRight size={16} />
          </Link>
          <Link
            to={`/vacatures/${vac.id}`}
            className="flex-1 sm:flex-none border border-gray-200 text-gray-700 px-6 py-3 rounded-lg font-medium text-sm text-center hover:border-[#00897B] hover:text-[#00897B] transition-all inline-flex items-center justify-center gap-2"
          >
            Volledige vacature
            <ChevronRight size={16} />
          </Link>
        </div>
      </div>

      {/* Body */}
      <div className="p-6 md:p-8 space-y-8">
        <div>
          <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">
            Over de functie
          </h3>
          <p className="text-gray-700 leading-relaxed">
            {vac.description} Als {vac.title.toLowerCase()} word je onderdeel van een gedreven team
            dat zich inzet voor kwaliteit en resultaat. Je werkt nauw samen met ervaren
            professionals en krijgt de ruimte om je verder te ontwikkelen binnen een inspirerende
            werkomgeving.
          </p>
        </div>

        <div>
          <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">
            Kwalificaties
          </h3>
          <ul className="space-y-2.5">
            {extra.qualifications.map((q, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-gray-700">
                <CheckCircle size={16} className="text-[#00897B] mt-0.5 flex-shrink-0" />
                <span>{q}</span>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">
            Verantwoordelijkheden
          </h3>
          <ul className="space-y-2.5">
            {extra.responsibilities.map((r, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-gray-700">
                <span className="w-1.5 h-1.5 rounded-full bg-[#00897B] mt-2 flex-shrink-0" />
                <span>{r}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="bg-gradient-to-br from-[#00897B]/5 to-[#00bfa5]/5 border border-[#00897B]/15 rounded-xl p-6">
          <h3 className="text-sm font-semibold text-[#00897B] uppercase tracking-wider mb-3">
            Wat wij bieden
          </h3>
          <ul className="space-y-2.5">
            {extra.benefits.map((b, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-gray-700">
                <CheckCircle size={16} className="text-[#00897B] mt-0.5 flex-shrink-0" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Company block */}
        <div className="pt-6 border-t border-gray-100">
          <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4">
            Over de opdrachtgever
          </h3>
          <div className="flex items-start gap-4">
            <div
              className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 font-bold ${
                disciplineColors[vac.discipline] || 'bg-gray-800 text-white'
              }`}
            >
              {vac.discipline.slice(0, 2).toUpperCase()}
            </div>
            <div>
              <p className="font-semibold text-gray-900">{extra.company}</p>
              <p className="text-sm text-gray-500 mt-0.5">
                {vac.location}, Nederland • {vac.discipline}
              </p>
              <p className="text-sm text-gray-600 mt-3 leading-relaxed">
                Een toonaangevende speler binnen de {vac.discipline.toLowerCase()}-sector met een
                sterke focus op innovatie, duurzaamheid en ontwikkeling van medewerkers. Via
                Staffing Force krijgt u toegang tot deze uitdagende opdracht.
              </p>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const VacaturesPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDiscipline, setSelectedDiscipline] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [sortBy, setSortBy] = useState('relevant');
  const [selectedId, setSelectedId] = useState(vacaturesData[0]?.id ?? null);

  const disciplines = ['all', ...Array.from(new Set(vacaturesData.map((v) => v.discipline)))];
  const types = ['all', ...Array.from(new Set(vacaturesData.map((v) => v.type)))];
  const locations = ['all', ...Array.from(new Set(vacaturesData.map((v) => v.location)))];

  const filteredVacatures = useMemo(() => {
    let res = vacaturesData.filter((vac) => {
      const matchesSearch =
        vac.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        vac.description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesDiscipline = selectedDiscipline === 'all' || vac.discipline === selectedDiscipline;
      const matchesType = selectedType === 'all' || vac.type === selectedType;
      const matchesLocation = selectedLocation === 'all' || vac.location === selectedLocation;
      return matchesSearch && matchesDiscipline && matchesType && matchesLocation;
    });
    if (sortBy === 'recent') {
      res = [...res].sort((a, b) => new Date(b.date) - new Date(a.date));
    }
    return res;
  }, [searchTerm, selectedDiscipline, selectedType, selectedLocation, sortBy]);

  // Keep selection in sync with filtered results
  useEffect(() => {
    if (filteredVacatures.length === 0) {
      setSelectedId(null);
      return;
    }
    if (!filteredVacatures.find((v) => v.id === selectedId)) {
      setSelectedId(filteredVacatures[0].id);
    }
  }, [filteredVacatures, selectedId]);

  const selectedVacature = filteredVacatures.find((v) => v.id === selectedId) || null;

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedDiscipline('all');
    setSelectedType('all');
    setSelectedLocation('all');
  };

  const hasActiveFilters =
    searchTerm || selectedDiscipline !== 'all' || selectedType !== 'all' || selectedLocation !== 'all';

  return (
    <main className="pt-28 min-h-screen bg-gray-50">
      <SEO
        title="Finance & HR Vacatures – Staffing Force Nederland"
        description="Bekijk alle openstaande vacatures in Finance, HR en Duurzaamheid. Interim, detavast of vast bij toonaangevende organisaties. Vind uw volgende uitdaging bij Staffing Force."
        keywords="vacatures, finance, HR, duurzaamheid, banen, interim, detavast, Amsterdam"
      />

      <PageHero
        label="Carrièremogelijkheden"
        title="Vacatures"
        description="Ontdek onze openstaande posities in Finance, HR en Duurzaamheid. Van interim opdrachten tot vaste functies bij toonaangevende organisaties."
        image="https://images.unsplash.com/photo-1497366216548-37526070297c?w=1920&q=85&auto=format&fit=crop"
      />

      {/* Search & Filters */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 -mt-12 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-white rounded-2xl shadow-xl p-5 md:p-6 border border-gray-100"
        >
          <div className="flex flex-col lg:flex-row gap-3">
            <div className="flex-1 relative">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 z-10" />
              <Input
                placeholder="Zoek op functie of trefwoord..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-11 border-gray-200 focus:border-[#00897B] focus:ring-[#00897B]/20 bg-white"
                data-testid="vacature-search-input"
              />
            </div>
            <Select value={selectedDiscipline} onValueChange={setSelectedDiscipline}>
              <SelectTrigger className="w-full lg:w-44 h-11 border-gray-200 bg-white" data-testid="filter-discipline">
                <SelectValue placeholder="Discipline" />
              </SelectTrigger>
              <SelectContent>
                {disciplines.map((d) => (
                  <SelectItem key={d} value={d}>
                    {d === 'all' ? 'Alle disciplines' : d}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={selectedType} onValueChange={setSelectedType}>
              <SelectTrigger className="w-full lg:w-36 h-11 border-gray-200 bg-white" data-testid="filter-type">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                {types.map((t) => (
                  <SelectItem key={t} value={t}>
                    {t === 'all' ? 'Alle types' : t}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={selectedLocation} onValueChange={setSelectedLocation}>
              <SelectTrigger className="w-full lg:w-40 h-11 border-gray-200 bg-white" data-testid="filter-location">
                <SelectValue placeholder="Locatie" />
              </SelectTrigger>
              <SelectContent>
                {locations.map((l) => (
                  <SelectItem key={l} value={l}>
                    {l === 'all' ? 'Alle locaties' : l}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {hasActiveFilters && (
            <div className="mt-4 flex items-center gap-3">
              <span className="text-sm text-gray-500">
                {filteredVacatures.length} resultaten gevonden
              </span>
              <button
                onClick={clearFilters}
                className="flex items-center gap-1 text-sm text-[#00897B] hover:text-[#00796B] transition-colors font-medium"
              >
                <X size={14} />
                Filters wissen
              </button>
            </div>
          )}
        </motion.div>
      </section>

      {/* Split Content: List + Detail */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-10">
        {/* Count + Sort */}
        <div className="flex items-center justify-between mb-5">
          <p className="text-sm text-gray-600" data-testid="vacature-count">
            <span className="font-semibold text-gray-900">{filteredVacatures.length}</span>{' '}
            vacatures
          </p>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 hidden sm:inline">Sorteer:</span>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="h-9 w-40 border-gray-200 bg-white text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="relevant">Meest relevant</SelectItem>
                <SelectItem value="recent">Meest recent</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {filteredVacatures.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-20 bg-white rounded-2xl border border-gray-100"
          >
            <Search size={48} className="mx-auto text-gray-300 mb-4" />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">
              Geen vacatures gevonden
            </h3>
            <p className="text-gray-500">Probeer andere zoektermen of filters.</p>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            {/* Left: List */}
            <div className="lg:col-span-2" data-testid="vacature-list">
              <div className="space-y-3 lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto lg:pr-3 custom-scrollbar">
                <AnimatePresence>
                  {filteredVacatures.map((vac) => (
                    <VacatureListCard
                      key={vac.id}
                      vac={vac}
                      isSelected={selectedId === vac.id}
                      onClick={() => setSelectedId(vac.id)}
                    />
                  ))}
                </AnimatePresence>
              </div>
            </div>

            {/* Right: Detail (sticky on desktop) */}
            <div className="lg:col-span-3">
              <div className="lg:sticky lg:top-24">
                <VacatureDetail vac={selectedVacature} />
              </div>
            </div>
          </div>
        )}
      </section>
    </main>
  );
};

export default VacaturesPage;
